import { Map, OrderedSet } from 'immutable';

import * as constants from './constants';
import * as types from './types';
import { GET_ORGANIZATION } from './../general/types';
import { getFilterPayloadAdapter } from './../../api-adapter';
import { deduplicateItems } from '../common/filters/utils';
import { parseToUsersMap } from './utils';

const initialState = Map({
  selectedDateFilter: Map({
    type: 'relative',
    value: 7 * 24 * 60 * 60,
  }),
  errors: Map({
    getTests: null,
    getOrganization: null,
    getMetrics: null,
  }),
  filters: Map().withMutations((map) => {
    constants.FILTERS_MAP.mapKeys((key) => {
      map.set(key, Map({
        items: OrderedSet(),
        selectedItems: OrderedSet(),
        loading: false,
        searchValue: '',
      }));
    });
  }),
});

const filtersReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case types.CLEAR_SELECTED_FILTERS:
      return state.update('filters', (filtersValue) => (
        filtersValue.map((filter) => filter.set('selectedItems', OrderedSet()))
      )).set('selectedDateFilter', Map({
        type: 'relative',
        value: 7 * 24 * 60 * 60,
      }));
    case types.SELECT_FILTER_ITEMS:
      return state.setIn(['filters', payload.name, 'selectedItems'], payload.items);
    case types.SELECT_DATE_FILTER:
      return state.set('selectedDateFilter', payload);
    case types.SET_FILTER_ITEMS:
      return state.setIn(['filters', payload.name, 'items'], payload.items);
    case types.REMOVE_FILTER_ITEM:
      return state.updateIn(['filters', payload.name, 'selectedItems'], (items) => (
        items.remove(payload.value)
      ));
    case types.SET_FILTERS_FROM_QUERY_PARAMS:
      return state.withMutations((newState) => {
        Object.entries(payload).forEach(([key, value]) => {
          if (!state.get('filters').has(key)) {
            return;
          }

          const valueArr = Array.isArray(value) ? value : [value];

          newState
            .updateIn(['filters', key, 'items'], (items) => items.union(valueArr))
            .updateIn(['filters', key, 'selectedItems'], (items) => items.union(valueArr));
        });
      });
    case types.GET_FILTERS.REQUEST:
      return state;
    case types.GET_FILTERS.SUCCESS:
      return state.withMutations((newState) => {
        Object
          .entries(getFilterPayloadAdapter(payload.aggs))
          .forEach(([key, items]) => {
            newState.setIn(
              ['filters', key, 'items'],
              // filter out all items that are empty strings or null/undefined
              OrderedSet(items.map((item) => item.name).filter((item) => !!item)),
            );
          });
      });
    case types.GET_FILTERS.FAILURE:
      // skipping this error for now as it does not prevent user from anything,
      // just fails to narrow down the possible options
      return state;
    case GET_ORGANIZATION.SUCCESS:
      return state.updateIn(['filters', 'owners', 'items'], (items) => {
        let newItems = OrderedSet();

        if (payload.is_ancestor) {
          newItems = newItems.add('Organization');
        }

        return newItems.union(items).union(payload.users);
      });
    case types.FETCH_OWNERS.SUCCESS: {
      return state.withMutations((newState) => {
        newState
          .setIn(['filters', 'owners', 'items'], payload.items)
          .setIn(['filters', 'owners', 'selectedItems'], payload.selectedItems);
      });
    }
    case types.FETCH_OWNERS_FILTERED.SUCCESS:
      return state.updateIn(['filters', 'owners', 'items'], (items) => {
        const users = parseToUsersMap(payload.results);
        const newItems = OrderedSet(items).merge(users);

        return deduplicateItems(newItems);
      });
    case types.SEARCH_METADATA.REQUEST:
      return state.setIn(['filters', payload, 'loading'], true);
    case types.SEARCH_METADATA.SUCCESS:
      return state.withMutations((newState) => {
        Object
          .entries(getFilterPayloadAdapter(payload.aggs))
          .forEach(([key, items]) => {
            const fetchedValues = items.map((item) => item.name).filter((item) => !!item);
            newState
              .updateIn(['filters', key, 'items'], (existingItems) => existingItems.union(fetchedValues))
              .setIn(['filters', key, 'loading'], false);
          });
      });
    case types.SEARCH_METADATA.FAILURE:
      return state.setIn(['filters', payload, 'loading'], false);
    default:
      return state;
  }
};

export default filtersReducer;
export { initialState };
